import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CreditCard,
  Plus,
  Wallet,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { CollectPaymentModal } from '@/pages/payments/CollectPaymentModal';
import { listFacilities, listPlanTypes, listUsers, type AdminUser } from '@/services/admin.service';
import { listTemplates } from '@/services/memberships.service';
import type { FilterConfig, FilterOption } from '@/types/filters.types';
import {
  getPaymentStats,
  getPayments,
  listCredits,
  listQuoteItems,
  listPaymentMethodOptions,
} from '@/services/payments.service';
import {
  formatCurrency,
  formatDate,
  getPaymentPatientName,
  getPaymentStatusDisplay,
  toCreditRows,
} from './paymentsShared';
import { buildApiParams } from '@/lib/filterRegistry';
import {
  PAYMENT_STATUS_OPTIONS,
  useFacilityOverrideValues,
  useLocalDashboardFilters,
} from '@/pages/dashboard/dashboardFilters';

const DASHBOARD_PAYMENT_PAGE_SIZE = 6;
const DASHBOARD_LIST_SIZE = 4;

function buildPaymentFilterConfigs(doctorLabel: string, coordLabel: string): FilterConfig[] {
  return [
    { key: 'search', label: 'Search', type: 'search', allLabel: 'Search payments...' },
    { key: 'daterange', label: 'Date', type: 'daterange' },
    { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
    { key: 'payment-type', label: 'Payment Type', type: 'select', allLabel: 'All types', multi: true },
    { key: 'payment-status', label: 'Status', type: 'select', allLabel: 'All statuses', multi: true },
    { key: 'plan-type', label: 'Plan Type', type: 'select', allLabel: 'All plan types', multi: true },
    { key: 'product', label: 'Product', type: 'select', allLabel: 'All products', multi: true, collapsibleGroups: true },
    { key: 'membership-type', label: 'Membership', type: 'select', allLabel: 'All memberships', multi: true },
    { key: 'package-type', label: 'Package', type: 'select', allLabel: 'All packages', multi: true },
    { key: 'doctor', label: doctorLabel, type: 'select', allLabel: `All ${doctorLabel.toLowerCase()}s`, multi: true },
    { key: 'coordinator', label: coordLabel, type: 'select', allLabel: `All ${coordLabel.toLowerCase()}s`, multi: true },
    { key: 'users', label: 'User', type: 'select', allLabel: 'All users', multi: true },
  ];
}

function SnapshotEmptyState({ message }: { message: string }) {
  return <p className="text-sm text-slate-500">{message}</p>;
}

export default function PaymentsDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userTz = useUserTimezone();
  const updateAIContext = usePageContext('Payments');
  const [showRecordModal, setShowRecordModal] = useState(false);

  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    () => buildPaymentFilterConfigs(doctorLabel, coordLabel),
    [doctorLabel, coordLabel],
  );

  const filters = useLocalDashboardFilters(filterConfigs, 'payments');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filters.values);
  const paymentFilters = useMemo(
    () => buildApiParams(effectiveValues, filters.negations),
    [effectiveValues, filters.negations],
  );

  const { data: paymentStats } = useQuery({
    queryKey: ['payments-stats', paymentFilters],
    queryFn: () => getPaymentStats(paymentFilters),
    staleTime: 60_000,
  });

  const { data: recentPaymentsData, isLoading: recentPaymentsLoading } = useQuery({
    queryKey: ['payments-dashboard-recent', paymentFilters],
    queryFn: () => getPayments(paymentFilters, { page: 1, pageSize: DASHBOARD_PAYMENT_PAGE_SIZE }),
    staleTime: 30_000,
  });

  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: listFacilities,
    staleTime: 5 * 60_000,
  });

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60_000,
  });

  const { data: quoteItems } = useQuery({
    queryKey: ['quote-items'],
    queryFn: () => listQuoteItems(),
    staleTime: 5 * 60_000,
  });

  const { data: planTypes } = useQuery({
    queryKey: ['plan-types'],
    queryFn: listPlanTypes,
    staleTime: 5 * 60_000,
  });

  const { data: membershipTemplates } = useQuery({
    queryKey: ['membership-templates-filter'],
    queryFn: () => listTemplates({ incrementType: 1, limit: 500 }),
    staleTime: 5 * 60_000,
  });

  const { data: packageTemplates } = useQuery({
    queryKey: ['package-templates-filter'],
    queryFn: () => listTemplates({ incrementType: 2, limit: 500 }),
    staleTime: 5 * 60_000,
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
    staleTime: 5 * 60_000,
  });

  const { data: credits, isLoading: creditsLoading } = useQuery({
    queryKey: ['credits'],
    queryFn: () => listCredits(),
    staleTime: 30_000,
  });

  const recentPayments = recentPaymentsData?.data ?? [];
  const creditRows = useMemo(() => toCreditRows(credits), [credits]);
  const activeCredits = useMemo(
    () => creditRows.filter((row) => row.void !== '1' && row.remaining > 0),
    [creditRows],
  );
  const remainingCreditValue = useMemo(
    () => activeCredits.reduce((sum, row) => sum + row.remaining, 0),
    [activeCredits],
  );
  const doctors = useMemo(
    () =>
      (users ?? [])
        .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('doctor'))
        .map((u: AdminUser) => ({ id: u.userId, name: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' })),
    [users],
  );

  const coordinators = useMemo(
    () =>
      (users ?? [])
        .filter((u: AdminUser) => u.active && u.role && u.role.toLowerCase().includes('coordinator'))
        .map((u: AdminUser) => ({ id: u.userId, name: `${u.firstName} ${u.lastName}`.trim() || 'Unknown' })),
    [users],
  );

  const filterOptions = useMemo<Record<string, FilterOption[]>>(() => ({
    'payment-type': [
      ...(paymentMethods ?? [])
        .filter((m) => !m.name.toLowerCase().includes('gift'))
        .map((m) => ({ value: String(m.paymentMethodID), label: m.name })),
      { value: 'gift_card', label: 'Gift Card' },
      { value: 'comp', label: 'Comp' },
    ],
    'payment-status': PAYMENT_STATUS_OPTIONS,
    facility: (facilities ?? []).filter((f) => f.active).map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
    doctor: doctors.map((d) => ({ value: String(d.id), label: d.name })),
    coordinator: coordinators.map((c) => ({ value: String(c.id), label: c.name })),
    product: [...(quoteItems ?? [])]
      .filter((qi) => !qi.name.includes('----'))
      .sort((a, b) => {
        const catA = (a.categoryTitle ?? 'Other').toLowerCase();
        const catB = (b.categoryTitle ?? 'Other').toLowerCase();
        if (catA !== catB) return catA.localeCompare(catB);
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      })
      .map((qi) => ({ value: String(qi.itemId), label: qi.name, group: qi.categoryTitle ?? 'Other' })),
    'plan-type': (planTypes ?? []).filter((pt) => pt.active).map((pt) => ({ value: String(pt.planTypeId), label: pt.name })),
    'membership-type': (membershipTemplates ?? []).filter((t) => !t.title.includes('----')).map((t) => ({ value: String(t.templateId), label: t.title })),
    'package-type': (packageTemplates ?? []).filter((t) => !t.title.includes('----')).map((t) => ({ value: String(t.templateId), label: t.title })),
    users: (users ?? []).map((u) => ({ value: String(u.userId), label: `${u.firstName} ${u.lastName}` })),
  }), [facilities, doctors, coordinators, quoteItems, planTypes, membershipTemplates, packageTemplates, users, paymentMethods]);

  useEffect(() => {
    updateAIContext({
      dataSummary: `Payments dashboard shows ${paymentStats?.total ?? 0} payments and ${activeCredits.length} active credits.`,
      extra: `Current payment volume totals ${formatCurrency(paymentStats?.totalAmount ?? 0)} with ${formatCurrency(remainingCreditValue)} in open credits.`,
    });
  }, [
    activeCredits.length,
    paymentStats?.total,
    paymentStats?.totalAmount,
    remainingCreditValue,
    updateAIContext,
  ]);

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={filterConfigs}
            values={effectiveValues}
            options={filterOptions}
            negations={filters.negations}
            onChange={filters.setFilter}
            onNegateChange={filters.setNegation}
            onClear={filters.clearFilter}
            lockedKeys={isFacilityLocked ? ['facility'] : undefined}
            storageKey="payments"
            showAIAssistant={false}
          />
        )}
      >
      <div className="space-y-6 overflow-y-auto p-6">
        <PageHeader
          title="Payments"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                leftIcon={<CreditCard className="h-4 w-4" />}
                onClick={() => navigate('/payments/all')}
              >
                Open All Payments
              </Button>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowRecordModal(true)}
              >
                Collect Payment
              </Button>
            </div>
          }
        />

        <MetricCardGrid columns={4}>
          <StatCard
            label="Total Records"
            value={paymentStats?.total ?? 0}
            subtitle="All captured payment records"
            tone="blue"
            icon={<CreditCard className="h-4 w-4" />}
            onClick={() => navigate('/payments/all')}
          />
          <StatCard
            label="Cash Collected"
            value={formatCurrency(paymentStats?.cashCollected ?? paymentStats?.totalAmount ?? 0)}
            subtitle="Money received, excl. gift-card tenders"
            tone="emerald"
            icon={<Wallet className="h-4 w-4" />}
            onClick={() => navigate('/payments/all')}
          />
          <StatCard
            label="Avg Payment"
            value={formatCurrency(paymentStats?.avgPayment ?? 0)}
            subtitle="Average captured payment size"
            tone="teal"
            icon={<CreditCard className="h-4 w-4" />}
            onClick={() => navigate('/payments/all')}
          />
          <StatCard
            label="Active Credits"
            value={activeCredits.length}
            subtitle={`${formatCurrency(remainingCreditValue)} still available`}
            tone="violet"
            icon={<Wallet className="h-4 w-4" />}
            onClick={() => navigate('/payments/credits')}
          />
        </MetricCardGrid>

        <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
          <Card>
            <CardHeader
              action={
                <Button
                  variant="link"
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                  onClick={() => navigate('/payments/all')}
                >
                  View All Payments
                </Button>
              }
            >
              <CardTitle>Recent Payments</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {recentPaymentsLoading ? (
                <SnapshotEmptyState message="Loading recent payments..." />
              ) : recentPayments.length === 0 ? (
                <SnapshotEmptyState message="No payments have been recorded yet." />
              ) : (
                recentPayments.map((payment) => (
                  <div
                    key={payment.paymentID}
                    className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {getPaymentPatientName(payment)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDate(payment.dtmDate, userTz)} · {payment.strPayMethod ?? 'Unspecified method'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">
                        {formatCurrency(payment.cardChargedAmount ?? payment.curAmount)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {getPaymentStatusDisplay(payment).label}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader
                action={
                  <Button
                    variant="link"
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                    onClick={() => navigate('/payments/credits')}
                  >
                    View Credits
                  </Button>
                }
              >
                <CardTitle>Credits Snapshot</CardTitle>
              </CardHeader>
              <CardBody className="space-y-4">
                {creditsLoading ? (
                  <SnapshotEmptyState message="Loading credits..." />
                ) : activeCredits.length === 0 ? (
                  <SnapshotEmptyState message="No active credits are available." />
                ) : (
                  activeCredits.slice(0, DASHBOARD_LIST_SIZE).map((credit) => (
                    <div
                      key={credit.paymentsCreditID}
                      className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {credit.creditTypeName ?? 'Credit'}
                        </p>
                        <p className="text-xs text-slate-500">
                          Patient {credit.patientID ? `#${credit.patientID}` : 'unassigned'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatCurrency(credit.remaining)}
                        </p>
                        <p className="text-xs text-slate-500">
                          Remaining
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      </ListPageLayout>

      <CollectPaymentModal
        isOpen={showRecordModal}
        onClose={() => setShowRecordModal(false)}
        onSuccess={() => {
          setShowRecordModal(false);
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          queryClient.invalidateQueries({ queryKey: ['payments-stats'] });
          queryClient.invalidateQueries({ queryKey: ['credits'] });
          queryClient.invalidateQueries({ queryKey: ['payments-dashboard-recent'] });
        }}
      />
    </>
  );
}
